import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Activity } from "lucide-react";

const STATUS_COLORS = {
    ok: "bg-green-500",
    warning: "bg-yellow-500",
    grace: "bg-orange-500",
    throttled: "bg-red-400",
    blocked: "bg-red-600",
};

const STATUS_LABELS = {
    ok: "OK",
    warning: "Warning",
    grace: "Grace Period",
    throttled: "Throttled",
    blocked: "Blocked",
};

export default function UsageCard({ data }) {
    if (!data) return null;

    const { usage, limits, concurrency } = data;
    const percentage = limits.monthlyLimit ? Math.round((usage.count / limits.monthlyLimit) * 100) : 0;
    const barWidth = limits.isUnlimited ? 0 : Math.min(percentage, 100);

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                        <Activity className="h-5 w-5" />
                        Usage This Month
                    </span>
                    <Badge variant={usage.status === "ok" ? "default" : "destructive"}>
                        {STATUS_LABELS[usage.status] || usage.status}
                    </Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {limits.isUnlimited ? (
                    <p className="text-sm text-muted-foreground">Unlimited plan — no usage limits</p>
                ) : (
                    <>
                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span>{usage.count.toLocaleString()} executions</span>
                                <span>{limits.monthlyLimit?.toLocaleString()} limit</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-3">
                                <div
                                    className={`h-3 rounded-full transition-all ${STATUS_COLORS[usage.status]}`}
                                    style={{ width: `${barWidth}%` }}
                                />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{percentage}% used</p>
                        </div>
                        {limits.overrideLimit !== null && (
                            <p className="text-xs text-amber-600 font-medium">
                                Admin override active: {limits.overrideLimit.toLocaleString()} limit
                            </p>
                        )}
                    </>
                )}
                <div className="grid grid-cols-3 gap-4 pt-2 border-t">
                    <div>
                        <p className="text-xs text-muted-foreground">Runtime</p>
                        <p className="text-sm font-medium">{(usage.runtimeMs / 1000).toFixed(1)}s</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Est. Cost</p>
                        <p className="text-sm font-medium">${usage.costEstimate}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Running Now</p>
                        <p className="text-sm font-medium">
                            {concurrency.running}{concurrency.maxConcurrent ? `/${concurrency.maxConcurrent}` : ""}
                        </p>
                    </div>
                </div>
                {usage.month && (
                    <p className="text-xs text-muted-foreground">Tracking month: {usage.month}</p>
                )}
            </CardContent>
        </Card>
    );
}
